import React, { useMemo, useState } from 'react';

type BulkUploadResult = {
  created: number;
  skipped: number;
  errors: any[];
};

type Props = {
  templateHeaders: string[];
  templateFilename: string;
  processAndUpload: (file: File) => Promise<BulkUploadResult>;
};

export const BulkUserUpload: React.FC<Props> = ({
  templateHeaders,
  templateFilename,
  processAndUpload,
}) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BulkUploadResult | null>(null);
  const [fatalError, setFatalError] = useState<string | null>(null);

  const rejectedCount = useMemo(() => (result?.errors ? result.errors.length : 0), [result]);

  const downloadTemplate = () => {
    const csv = `${templateHeaders.join(',')}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = templateFilename || 'template.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    // allow re-select same file
    e.target.value = '';
    if (!file) return;

    setBusy(true);
    setResult(null);
    setFatalError(null);

    try {
      const out = await processAndUpload(file);

      const safe: BulkUploadResult = {
        created: Number(out?.created || 0),
        skipped: Number(out?.skipped || 0),
        errors: Array.isArray(out?.errors) ? out.errors : [],
      };

      setResult(safe);
    } catch (err: any) {
      setFatalError(String(err?.message || err || 'bulk_upload_failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" onClick={downloadTemplate} disabled={busy}>
          Download CSV template
        </button>

        <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          <span>Upload CSV</span>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={onFileSelected}
            disabled={busy}
          />
        </label>

        {busy && <span>Processing…</span>}
      </div>

      {fatalError && (
        <div style={{ marginTop: 10, color: '#b91c1c' }}>
          {fatalError}
        </div>
      )}

      {result && (
        <div style={{ marginTop: 10 }}>
          <div>
            Added {result.created}. Skipped {result.skipped}. Rejected {rejectedCount}.
          </div>

          {rejectedCount > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary>Show rejected rows</summary>
              <div style={{ marginTop: 8, maxHeight: 220, overflow: 'auto' }}>
                <pre style={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(result.errors.slice(0, 200), null, 2)}
                </pre>
                {rejectedCount > 200 && (
                  <div>Showing first 200 of {rejectedCount} errors.</div>
                )}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
};